/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */
import { SxProps, Theme } from '@mui/material';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

export const headerSearchSX = ({
	theme,
	expand,
}: {
	theme?: Theme;
	isMobile?: boolean;
	expand?: boolean;
}): SxProps<Theme> => ({
	width: '100%',
	minWidth: '65px',
	paddingLeft: '15px',
	paddingRight: '15px',

	padding: expand ? 0 : 'auto',
	backgroundColor: expand ? 'white' : 'inherit',

	'.MuiInputBase-root.MuiOutlinedInput-root': {
		borderRadius: 0,

		'&:hover:not(.Mui-focused) ': {
			'.MuiOutlinedInput-notchedOutline': {
				borderColor: theme?.palette.grey[200],
			},
		},
	},

	'.MuiOutlinedInput-notchedOutline': {
		borderColor: theme?.palette.grey[100],
	},

	'.MuiOutlinedInput-input': {
		'&::placeholder': {
			color: theme?.palette.grey[200],
		},
		'&:focus': {
			'&::placeholder': {
				color: theme?.palette.grey[100],
			},
		},
		'&::-webkit-input-placeholder': {
			opacity: 0.8,
		},
	},

	'.MuiButtonBase-root.MuiIconButton-root:hover': {
		border: '0',
	},
	...(theme && {
		[theme?.breakpoints.up('xs')]: {
			padding: '0px',
		},
	}),
});

export const headerSearchIconContainer = (theme: Theme, expand: boolean): SxProps => ({
	[theme.breakpoints.up('xs')]: {
		...(!expand && { position: 'absolute', right: 8 }),
	},
});

export const headerSearchIconSX = (theme: Theme, expand: boolean): SxProps => ({
	paddingRight: '15px',
	position: expand ? 'relative' : '',
	...(expand && { right: 12 }),
	backgroundColor: expand ? 'primary.main' : 'white',
	color: expand ? 'white' : 'primary.main',
	[theme.breakpoints.up('xs')]: {
		padding: '0px',
	},
});

export const autocompleteSX: Record<string, SxProps<Theme>> = {
	parentContainer: {
		width: '100%',
	},
	listContainer: {
		width: '100vw',
		position: 'absolute',
		left: '-10vw',
		paddingLeft: '8vw',
	},
	groupContainer: {
		borderTop: '1px solid',
		borderTopColor: 'grey.50',
		pt: '5px',
	},
	groupTitle: {
		color: 'grey.100',
		fontWeight: OpenSansFontWeights.semibold,
	},
	groupContentContainer: {
		pt: '11px',
		display: 'flex',
		flexDirection: 'column',
		gap: '5px',
	},
	groupContentContainerKeyword: {
		display: 'flex',
		flexDirection: 'column',
		gap: '5px',
	},
	groupContentContainerRecent: {
		pt: '11px',
		display: { xs: 'flex', md: 'grid' },
		flexDirection: { xs: 'column', md: 'row' },
		gap: '5px',
		gridTemplateColumns: 'repeat(4, 1fr)',
	},
	optionContainer: {
		color: 'grey.500',
		padding: 0,
		display: 'flex',
		gap: '5px',
	},
	optionIcon: {
		color: 'grey.200',
		fontWeight: '200',
		width: '24px',
		height: '24px',
	},
	optionKeyword: {
		whiteSpace: 'pre-wrap',
	},
	recentProductLink: {
		flex: '1',
	},
	recentProductContainer: {
		p: '10px 12px',
		display: 'flex',
		flexDirection: { xs: 'row', md: 'column' },
		alignItems: 'center',
		gap: '10px',
		width: '100%',
	},
	recentProductImage: {
		maxWidth: { xs: '60px', md: '90px' },
		maxHeight: { xs: '60px', md: '90px' },
		aspectRatio: '1 / 1',
		width: '100%',
	},
	recentProductNameContainer: {
		display: 'flex',
		flexDirection: 'column',
		gap: '5px',
		color: 'grey.500',
		width: '100%',
	},
	recentProductName: {
		display: '-webkit-box',
		WebkitLineClamp: { xs: 1, md: 2 },
		WebkitBoxOrient: 'vertical',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
	optionLabel: {
		color: 'grey.500',
	},
	paper: {
		display: 'flex',
		flexDirection: { xs: 'column', lg: 'row' },
		minWidth: { xs: '100%', lg: '680px' },
	},
	paperContainer: {
		width: '100%',
		p: '10px',
		'& .MuiAutocomplete-listbox': {
			padding: 0,
			display: 'flex',
			flexDirection: 'column',
			gap: '10px',
			maxHeight: 'none',
		},
		'& .MuiAutocomplete-listbox .MuiAutocomplete-option': {
			padding: 0,
			minHeight: 'auto',
		},
		display: 'flex',
		flexDirection: 'column',
		gap: '10px',
	},
	popularCategories: {
		borderTop: '1px solid',
		borderTopColor: 'grey.50',
		pt: '5px',
		display: 'flex',
		gap: '11px',
		flexDirection: 'column',
	},
	espotContainer: {
		width: { xs: '100%', lg: '40%' },
	},
	poper: {
		width: { xs: '100vw !important', md: '100% !important' },
		transform: 'translate3d(0px, 40px, 0px) !important',
	},
	backspace: {
		mr: { xs: '5px', md: '0' },
	},
};

export const headerSearchContainerSX = (theme: Theme, expand: boolean): SxProps<Theme> => ({
	position: expand ? 'fixed' : 'relative',
	top: expand ? 'max(env(safe-area-inset-top), 0px)' : 'auto',
	left: expand ? 0 : 'auto',
	right: expand ? 0 : 'auto',
	zIndex: expand ? theme.zIndex.modal + 1 : 'auto',
	width: expand ? '90dvw' : '100%',
	minWidth: '65px',
});

export const headerSearchTextfieldSX = (expand: boolean): SxProps<Theme> => ({
	padding: 0,
	left: expand ? '10vw' : 0,
	width: '100%',
	minWidth: '65px',
});

export const headerSearchTextfieldInputPropsSX = (expand: boolean): SxProps<Theme> => ({
	padding: expand ? '0px !important' : 'auto',
});

export const headerSearchTextfieldInputSlotPropsSX = (): SxProps<Theme> => ({
	position: 'relative',
	left: '-24px',
});

export const headerSearchIconArrowBackSX = (): SxProps<Theme> => ({
	position: 'relative',
	left: '-8vw',
});
